import { billetStatus } from "@/shared/selectors/BilletStatusSelector";
import {
	eventTypes,
	productionTypes,
} from "@/shared/selectors/EventTypeSelector";
import { getMomentTz, TimeService } from "@dexteel/mesf-core";
import FullCalendar from "@fullcalendar/react";
import { useCallback } from "react";
import { EventSegment } from "../models/Event";
import { isDayInCurrentMonth } from "../utils/calendarUtils";

const moment = getMomentTz();

export const calculateDeadline = (deadline: Date | null) => {
	if (!deadline) return "Maximum date and time to complete the event";

	const deadlineDate = deadline instanceof Date ? deadline : new Date(deadline);
	const now = new Date();
	const diffMs = deadlineDate.getTime() - now.getTime();

	// If deadline has passed
	if (diffMs < 0) {
		const absDiffMs = Math.abs(diffMs);
		const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
		const hours = Math.floor(
			(absDiffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
		);
		const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));

		if (days > 0) {
			return `Overdue by ${days} day${days !== 1 ? "s" : ""}`;
		} else if (hours > 0) {
			return `Overdue by ${hours} hour${
				hours !== 1 ? "s" : ""
			} and ${minutes} minute${minutes !== 1 ? "s" : ""}`;
		} else {
			return `Overdue by ${minutes} minute${minutes !== 1 ? "s" : ""}`;
		}
	}

	// Calculate time remaining
	const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
	const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

	if (days > 0) {
		return `In ${days} day${days !== 1 ? "s" : ""}`;
	} else if (hours > 0) {
		return `In ${hours} hour${hours !== 1 ? "s" : ""} and ${minutes} minute${
			minutes !== 1 ? "s" : ""
		}`;
	} else {
		return `In ${minutes} minute${minutes !== 1 ? "s" : ""}`;
	}
};

const getDateAtStartOfDay = (date: Date): Date => {
	const serverTz = TimeService.getInstance().getServerTimeZone();

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const dateStr = `${year}-${month}-${day}`;

	return moment.tz(dateStr, "YYYY-MM-DD", serverTz).startOf("day").toDate();
};

const getDateAtEndOfDay = (date: Date): Date => {
	const serverTz = TimeService.getInstance().getServerTimeZone();

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const dateStr = `${year}-${month}-${day}`;

	return moment.tz(dateStr, "YYYY-MM-DD", serverTz).endOf("day").toDate();
};

export const useCalendarViewHandlers = ({
	start,
	setStart,
	setEnd,
	setCalendarView,
	setView,
	setIsViewingToday,
	setCalendarTitle,
	calendarRef,
}: {
	start: Date;
	setStart: (start: Date) => void;
	setEnd: (end: Date) => void;
	setCalendarView: (
		view: "dayGridMonth" | "timeGridWeek" | "timeGridDay",
	) => void;
	setView: (view: "calendar" | "list") => void;
	setIsViewingToday: (isViewingToday: boolean) => void;
	setCalendarTitle: (title: string) => void;
	calendarRef: React.RefObject<FullCalendar | null>;
}) => {
	const handleViewChange = useCallback(
		(
			newView: "dayGridMonth" | "timeGridWeek" | "timeGridDay",
			actualView: "calendar" | "list",
		) => {
			setCalendarView(newView);
			if (actualView === "calendar") {
				const calendarApi = calendarRef.current?.getApi();
				if (calendarApi) {
					calendarApi.changeView(newView);
				}
			} else {
				// Update date range for list view based on the new view mode
				const serverTz = TimeService.getInstance().getServerTimeZone();
				const currentStart = moment(start).tz(serverTz);
				let newStart: Date;
				let newEnd: Date;

				if (newView === "dayGridMonth") {
					// Add 7 days to ensure we're in the actual month being viewed (not a padding day)
					const actualDate = currentStart.clone().add(7, "days");
					newStart = actualDate.clone().startOf("month").toDate();
					newEnd = actualDate.clone().endOf("month").toDate();
				} else if (newView === "timeGridWeek") {
					// Add 3 days to ensure we're in the actual week being viewed
					const actualDate = currentStart.clone().add(3, "days");
					newStart = actualDate.clone().startOf("week").toDate();
					newEnd = actualDate.clone().endOf("week").toDate();
				} else {
					// timeGridDay
					newStart = currentStart.clone().startOf("day").toDate();
					newEnd = currentStart.clone().endOf("day").toDate();
				}

				setStart(newStart);
				setEnd(newEnd);

				// Update title
				const today = new Date();
				const isToday = today >= newStart && today <= newEnd;
				setIsViewingToday(isToday);

				if (newView === "dayGridMonth") {
					setCalendarTitle(
						newStart.toLocaleDateString("en-US", {
							month: "long",
							year: "numeric",
						}),
					);
				} else if (newView === "timeGridWeek") {
					setCalendarTitle(
						`${newStart.toLocaleDateString("en-US", {
							month: "short",
							day: "numeric",
						})} - ${newEnd.toLocaleDateString("en-US", {
							month: "short",
							day: "numeric",
							year: "numeric",
						})}`,
					);
				} else {
					setCalendarTitle(
						newStart.toLocaleDateString("en-US", {
							month: "long",
							day: "numeric",
							year: "numeric",
						}),
					);
				}
			}
		},
		[
			start,
			setCalendarView,
			setStart,
			setEnd,
			setIsViewingToday,
			setCalendarTitle,
		],
	);

	const handleListViewClick = useCallback(() => {
		setView("list");
	}, []);

	const handleCalendarViewClick = useCallback(() => {
		setView("calendar");
		setCalendarView("dayGridMonth");

		setTimeout(() => {
			const calendarApi = calendarRef.current?.getApi();
			if (calendarApi) {
				calendarApi.changeView("dayGridMonth");
			}
		}, 0);
	}, [calendarRef, setView, setCalendarTitle]);

	return {
		handleViewChange,
		handleListViewClick,
		handleCalendarViewClick,
	};
};

export const useEventHandlers = ({
	editable,
	hoveredBilletStatusCode,
	selectable,
	currentDate,
	onDateRangeSelect,
	setIsDragging,
	isDragging,
	setDragStartDate,
	dragStartDate,
	setDragEndDate,
	dragEndDate,
	swapProductionColors = false,
}: {
	editable: boolean;
	hoveredBilletStatusCode: string | null;
	selectable: boolean;
	currentDate: Date;
	onDateRangeSelect: (startDate: Date, endDate: Date) => void;
	setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
	isDragging: boolean;
	setDragStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
	dragStartDate: Date | null;
	setDragEndDate: React.Dispatch<React.SetStateAction<Date | null>>;
	dragEndDate: Date | null;
	swapProductionColors?: boolean;
}) => {
	const getEventStyle = useCallback(
		(segment: EventSegment): React.CSSProperties => {
			const { segStart, segEnd, isStart, isEnd, event, spanDays } = segment;

			const eventType = eventTypes.find((t) => t.code === event.EventTypeCode);
			const productionType = productionTypes.find(
				(t) => t.code === event.StatusCode,
			);
			const billetType = billetStatus.find(
				(t) => t.code === event.BilletsStatusCode,
			);

			const isHoveredBillet =
				hoveredBilletStatusCode &&
				event.BilletsStatusCode === hoveredBilletStatusCode;

			const backgroundColor =
				event.EventTypeCode === "P"
					? isHoveredBillet
						? (billetStatus.find((s) => s.code === event.BilletsStatusCode)
								?.backgroundColor ?? productionType?.backgroundColor)
						: swapProductionColors
							? billetType?.backgroundColor
							: productionType?.backgroundColor
					: eventType?.backgroundColor;
			const borderColor =
				event.EventTypeCode === "P"
					? productionType?.borderColor
					: eventType?.borderColor;
			const color =
				event.EventTypeCode === "P"
					? isHoveredBillet
						? "#404040"
						: productionType?.textColor
							? productionType?.textColor
							: eventType?.textColor
					: "";

			const baseStyle: React.CSSProperties = {
				position: "relative",
				overflow: "hidden",
				cursor: editable ? "move" : "pointer",
				borderRadius: "4px",
				padding: "2px 6px",
				marginBottom: "2px",
				fontSize: "0.75rem",
				whiteSpace: "nowrap",
				textOverflow: "ellipsis",
				backgroundColor,
				borderLeft:
					event.EventTypeCode === "P"
						? swapProductionColors
							? `30px solid ${productionType?.backgroundColor}`
							: `30px solid ${billetType?.backgroundColor}`
						: `3px solid ${borderColor}`,
				borderRight: `3px solid ${borderColor}`,
				color,
			};

			let width = 100;
			let marginLeft = 0;

			const momentStart = moment.tz(
				segStart,
				TimeService.getInstance().getServerTimeZone(),
			);
			const momentEnd = moment.tz(
				segEnd,
				TimeService.getInstance().getServerTimeZone(),
			);

			if (isStart && isEnd) {
				const startPercent =
					((momentStart.hour() * 60 + momentStart.minute()) / (24 * 60)) * 100;
				const durationMinutes =
					(momentEnd.valueOf() - momentStart.valueOf()) / (1000 * 60);
				const durationPercent = Math.min(
					(durationMinutes / (24 * 60)) * 100,
					100 - startPercent,
				);
				marginLeft = startPercent;
				width = Math.max(durationPercent, 5);
			} else if (isStart) {
				const startPercent =
					((momentStart.hour() * 60 + momentStart.minute()) / (24 * 60)) * 100;
				marginLeft = startPercent;
				width = 100 - startPercent;
			} else if (isEnd) {
				const endPercent =
					((momentEnd.hour() * 60 + momentEnd.minute()) / (24 * 60)) * 100;
				marginLeft = 0;
				width = Math.max(endPercent, 5);
			} else {
				marginLeft = 0;
				width = 100;
			}

			const result = {
				...baseStyle,
				width: `${width}%`,
				marginLeft: `${marginLeft}%`,
			};

			return result;
		},
		[editable, hoveredBilletStatusCode, swapProductionColors],
	);

	const handleDayMouseDown = useCallback(
		(day: Date) => {
			if (!selectable || !isDayInCurrentMonth(day, currentDate)) return;
			setIsDragging(true);
			setDragStartDate(day);
			setDragEndDate(day);
		},
		[selectable, currentDate],
	);

	const handleDayMouseOver = useCallback(
		(day: Date) => {
			if (!isDragging || !dragStartDate) return;
			if (day.getTime() >= dragStartDate.getTime()) {
				setDragEndDate(day);
			} else {
				setDragEndDate(dragStartDate);
				setDragStartDate(day);
			}
		},
		[isDragging, dragStartDate],
	);

	const handleDayMouseUp = useCallback(() => {
		if (!isDragging || !dragStartDate || !dragEndDate || !onDateRangeSelect)
			return;

		const startDate = getDateAtStartOfDay(
			dragStartDate.getTime() <= dragEndDate.getTime()
				? dragStartDate
				: dragEndDate,
		);
		const endDate = getDateAtEndOfDay(
			dragStartDate.getTime() <= dragEndDate.getTime()
				? dragEndDate
				: dragStartDate,
		);

		onDateRangeSelect(startDate, endDate);

		setIsDragging(false);
		setDragStartDate(null);
		setDragEndDate(null);
	}, [isDragging, dragStartDate, dragEndDate, onDateRangeSelect]);

	return {
		getEventStyle,
		handleDayMouseDown,
		handleDayMouseOver,
		handleDayMouseUp,
	};
};
